// CarrosselAvaliacoes.js — Faixa contínua com as avaliações recentes do site.
//
// Usa o `ui/Marquee.js` que JÁ existe (o mesmo motor da VitrineCapas): o loop é
// CSS puro, sem JS por frame, pausa no hover, tem máscara de fade nas bordas e
// para de vez com `prefers-reduced-motion`. Numa faixa que nunca para, cada
// frame calculado em JS custa bateria à página inteira.
//
// MÍNIMO DE ITENS — o detalhe que decide se a seção ajuda ou atrapalha:
// com 3 avaliações a repetição fica escancarada e a faixa passa a comunicar
// que o site é vazio, não que é movimentado. Abaixo do mínimo a seção
// simplesmente não renderiza — mesmo padrão da VitrineCapas
// (MINIMO_DE_CAPAS = 6) e do EspecialDaCasa sem livro.
//
// Depois que só quem comprou passou a poder avaliar, o volume cresce devagar.
// É esperado que esta seção fique invisível por um tempo; é preferível a uma
// vitrine com quatro depoimentos girando em círculo.
import React from "react";
import { Link } from "react-router-dom";
import useAvaliacoesRecentes from "../../hooks/useAvaliacoesRecentes";
import Marquee from "../ui/Marquee";
import Reveal from "../ui/Reveal";
import Stars from "../produto/paginaProduto/Stars";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const MINIMO_DE_AVALIACOES = 6;

const CardAvaliacao = ({ avaliacao }) => (
  <Link
    to={main + "livros/" + avaliacao.livro.id}
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    className="cartao cartao-clique group/dep block w-[320px] md:w-[380px] mx-2.5 my-2 p-4"
  >
    <div className="flex gap-3.5">
      {/* Capa pequena: é o que faz a pessoa reconhecer o livro antes de ler o
          texto. Proporção 3:4, a mesma de todo card do site. */}
      <div className="w-[56px] shrink-0 overflow-hidden rounded-md border border-insigne-sombra bg-insigne-navy-deep aspect-[3/4]">
        {avaliacao.livro.image ? (
          <img
            src={`${API_URL}public/${avaliacao.livro.image}`}
            alt={avaliacao.livro.titulo}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="bi bi-book text-white/30" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h4 className="text-sm font-bold text-white leading-snug line-clamp-1 group-hover/dep:text-insigne-orange transition-colors">
          {avaliacao.livro.titulo}
        </h4>
        <Stars value={avaliacao.nota} size="text-xs" className="mt-1" />
        {/* Três linhas no máximo: a faixa está em movimento, e um parágrafo
            inteiro passando de lado não se lê. */}
        <p className="text-white/70 text-xs leading-relaxed mt-1.5 line-clamp-3">
          “{avaliacao.comentario}”
        </p>
        <p className="text-white/40 text-[11px] mt-1.5">{avaliacao.usuario_nome}</p>
      </div>
    </div>
  </Link>
);

const CarrosselAvaliacoes = () => {
  const { avaliacoes, loading } = useAvaliacoesRecentes(24);

  // Enquanto carrega não há esqueleto: a seção pode não existir, e um
  // placeholder que some deixaria a home pulando.
  if (loading || avaliacoes.length < MINIMO_DE_AVALIACOES) return null;

  return (
    <section id="avaliacoes-recentes" className="px-4 md:px-8 py-10">
      <div className="max-w-[1280px] mx-auto">
        <Reveal
          as="h2"
          className="flex items-center gap-3 text-xl md:text-2xl font-bold text-white mb-6"
        >
          <i className="bi bi-chat-quote-fill text-white" />
          O que os leitores estão dizendo
        </Reveal>
      </div>

      {/* A faixa vaza das margens de propósito: é o que faz ela parecer
          contínua em vez de uma lista dentro de uma caixa. */}
      <Reveal>
        <Marquee velocidade={70}>
          {avaliacoes.map((a) => (
            <CardAvaliacao key={a.id} avaliacao={a} />
          ))}
        </Marquee>
      </Reveal>
    </section>
  );
};

export default CarrosselAvaliacoes;
